import { FC, ReactNode } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Alert } from "src/domains/shared/components/Alert";
import { isCountKnown } from "src/domains/shared/utils/queryRenderState";
import { projectHistoryQueryKey } from "src/domains/myPageContent/projectHistory/history-query/projectHistory.queries";
import { BranchFade, StateSlot } from "src/domains/shared/components/StateTransition";
import { MYPAGE_CARD } from "./myPageStyles";
import { useProjectHistory } from "./hooks/useProjectHistory";
import { HistoryTimelineList } from "./components/HistoryTimelineList";
import { HistoryFilterBar } from "./components/HistoryFilterBar";

export const ProjectHistory: FC = () => {
  const { t } = useTranslation("projects");
  const history = useProjectHistory();
  const { projectListRenderState } = history;
  const retryLabel = t("queryState.retry", { ns: "common" });

  // 프로젝트 목록이 미종결이거나 이미 도착한 구간에서는 타임라인이 자기 상태(로딩·권한없음·
  // 실패·빈 결과)를 스스로 그린다 — 앞단 조회 갈래는 그 영역을 대체하지 않는다.
  // 타임라인을 그리는 두 갈래(pending·ready)는 같은 자식을 반환해 갈래 전환이 목록을
  // 다시 마운트하지 않게 한다(스크롤·페이지네이션 상태 보존).
  const timeline = (
    <HistoryTimelineList
      items={history.flatItems}
      // 프로젝트 전환 중에는 아직 이 프로젝트의 데이터가 아니므로 로딩으로 본다
      // (필터 전환은 같은 프로젝트라 keepPreviousData 로 목록을 유지 — 깜빡임 없음)
      isLoading={history.isHistoryLoading || history.hasUnresolvedProjectSwitch}
      isForbidden={history.isForbidden}
      isGeneralError={history.isGeneralError}
      isFilterActive={history.historyTypeFilter !== null}
      hasNextPage={history.hasNextPage}
      isFetchingNextPage={history.isFetchingNextPage}
      fetchNextPage={history.fetchNextPage}
      onRetry={history.refetchHistory}
      // 조회 컨텍스트가 바뀔 때만 바뀌도록 queryKey 와 같은 함수에서 파생시킨다
      resetKey={projectHistoryQueryKey(history.selectedProject, history.historyTypeFilter).join(":")}
    />
  );

  const body: { key: string; node: ReactNode } = (() => {
    // 목록 조회 실패를 "소속된 프로젝트가 없습니다" 로 번역하면 사용자는 자기가 어떤
    // 프로젝트에도 속하지 않는다고 믿고 재시도할 생각조차 하지 못한다.
    if (projectListRenderState.state === "error") {
      return {
        key: "projects-error",
        node: (
          <StateSlot
            tone="error"
            icon="alert"
            title={t("queryState.projectListLoadFailed", { ns: "projects" })}
            retryLabel={retryLabel}
            onRetry={() => history.refetchProjects()}
          />
        ),
      };
    }
    if (projectListRenderState.state === "empty") {
      return {
        key: "projects-empty",
        node: <StateSlot tone="neutral" icon="inbox" title={t("myPage.history.noProject", { ns: "projects" })} />,
      };
    }
    return { key: "timeline", node: timeline };
  })();

  // 저하 통지는 갈래 매핑 바깥에 둔다 — `empty + degraded`("0건 확인 뒤 재조회 실패")에서도
  // 통지가 남아야 화면이 "소속 프로젝트 없음" 을 확정 사실로 주장하지 않는다.
  const showsDegradedNotice = projectListRenderState.degraded && isCountKnown(projectListRenderState);

  return (
    <Box sx={{ flex: 1, minHeight: 0, padding: "16px", display: "flex" }}>
      <Box sx={{ ...MYPAGE_CARD, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <HistoryFilterBar
          projectOptions={history.projectOptions}
          selectedProject={history.selectedProject}
          onSelectProject={history.setSelectedProject}
          historyTypeFilter={history.historyTypeFilter}
          onSelectHistoryType={history.setHistoryTypeFilter}
          projectsRenderState={projectListRenderState}
        />
        {showsDegradedNotice && (
          <Box sx={{ padding: "8px 12px 0" }}>
            <Alert
              type="warning"
              message={t("queryState.projectListLoadFailed", { ns: "projects" })}
              actionLabel={retryLabel}
              onActionClick={() => history.refetchProjects()}
            />
          </Box>
        )}
        <BranchFade key={body.key}>{body.node}</BranchFade>
      </Box>
    </Box>
  );
};
